// Call lists (JT1-1827). Mirrors backend/src/modules/tenant/call-lists.

export enum CallOutcome {
  CONNECTED = 'connected',
  VOICEMAIL = 'voicemail',
  NO_ANSWER = 'no_answer',
  WRONG_NUMBER = 'wrong_number',
  DO_NOT_CALL = 'do_not_call',
}

export enum CallListEntryStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  UNREACHABLE = 'unreachable',
  WRONG_NUMBER = 'wrong_number',
  DO_NOT_CALL = 'do_not_call',
  REMOVED = 'removed',
}

export enum CallNextReason {
  FIRST_CALL = 'first_call',
  RETRY = 'retry',
  FOLLOW_UP = 'follow_up',
  NEW_ROUND = 'new_round',
  DEFERRED = 'deferred',
}

export enum CallNumberType {
  PHONE = 'phone',
  SWITCHBOARD = 'switchboard',
  CUSTOM_FIELD = 'custom_field',
  OTHER = 'other',
}

export enum CallQueueView {
  DUE = 'due',
  UPCOMING = 'upcoming',
  DONE = 'done',
}

export enum CallListPersonType {
  CONTACT = 'contact',
  CANDIDATE = 'candidate',
}

export enum NextCallPreset {
  THREE_BUSINESS_DAYS = 'three_business_days',
  ONE_WEEK = 'one_week',
  TWO_WEEKS = 'two_weeks',
  ONE_MONTH = 'one_month',
  TWO_MONTHS = 'two_months',
  NEVER = 'never',
  DATE = 'date',
}

export enum FirstCallWhen {
  TODAY = 'today',
  TOMORROW = 'tomorrow',
  NEXT_WEEK = 'next_week',
  DATE = 'date',
}

export enum CallListAddedFrom {
  CONTACT_PROFILE = 'contact_profile',
  CANDIDATE_PROFILE = 'candidate_profile',
  CONTACT_LIST = 'contact_list',
  CANDIDATE_LIST = 'candidate_list',
  CALL_LIST = 'call_list',
}

export enum CallAttemptGapUnit {
  BUSINESS_DAYS = 'business_days',
  CALENDAR_DAYS = 'calendar_days',
}

export enum CallListAfterLastAttempt {
  UNREACHABLE = 'unreachable',
  PARK = 'park',
}

export enum CallListAfterConnected {
  ONE_WEEK = 'one_week',
  TWO_WEEKS = 'two_weeks',
  ONE_MONTH = 'one_month',
  TWO_MONTHS = 'two_months',
  ASK = 'ask',
}

export enum CallListAfterVoicemail {
  NEXT_BUSINESS_DAY = 'next_business_day',
  ATTEMPT_GAP = 'attempt_gap',
  ONE_WEEK = 'one_week',
}

export enum CallListAfterNoAnswer {
  LATER_TODAY = 'later_today',
  NEXT_BUSINESS_DAY = 'next_business_day',
  ATTEMPT_GAP = 'attempt_gap',
}

export enum CallListWrongNumberAction {
  DROP_AND_FLAG = 'drop_and_flag',
  DROP = 'drop',
}

export interface CallListAccount {
  id: string;
  first_name: string;
  last_name: string;
}

export interface CallListCounts {
  active: number;
  due_today: number;
  upcoming: number;
  done_today: number;
}

export interface CallListSettings {
  max_attempts: number;
  attempt_gap: number;
  attempt_gap_unit: CallAttemptGapUnit;
  after_last_attempt: CallListAfterLastAttempt;
  after_connected: CallListAfterConnected;
  after_voicemail: CallListAfterVoicemail;
  after_no_answer: CallListAfterNoAnswer;
  on_wrong_or_dnc: CallListWrongNumberAction;
  auto_advance: boolean;
  auto_dial: boolean;
  working_hours_only: boolean;
}

export interface CallList extends CallListSettings {
  id: string;
  name: string;
  owner: CallListAccount;
  /** False when an admin opens a team member's list: they can manage it, not call from it. */
  is_owner: boolean;
  counts: CallListCounts;
  created_at: string;
}

export interface CallListSummary extends CallListCounts {
  progress: { called: number; total: number };
}

export interface CallPerson {
  type: CallListPersonType;
  id: string;
  first_name: string;
  last_name: string;
  title: string | null;
  company: string | null;
  location: string | null;
  do_not_call: boolean;
}

export interface CallAttempt {
  id: string;
  cycle: number;
  attempt_number: number;
  max_attempts: number;
  outcome: CallOutcome;
  dialled_number: string | null;
  number_type: CallNumberType | null;
  number_label: string | null;
  started_at: string | null;
  duration_seconds: number | null;
  notes: string | null;
  next_call_at: string | null;
  created_at: string;
  account: CallListAccount | null;
  comment_id: string | null;
  task_id: string | null;
}

export interface CallListShared {
  entry_id: string;
  owner: CallListAccount;
  is_mine: boolean;
  list_name: string | null;
  added_at: string;
  last_attempt_at: string | null;
  next_call_at: string | null;
  attempt_number: number;
  max_attempts: number;
}

export interface CallListEntry {
  id: string;
  call_list_id: string;
  status: CallListEntryStatus;
  attempt_number: number;
  max_attempts: number;
  cycle: number;
  next_call_at: string | null;
  next_call_reason: CallNextReason;
  skipped_today: boolean;
  last_attempt_at: string | null;
  last_outcome: CallOutcome | null;
  dial_number_type: CallNumberType;
  dial_custom_field_config_id: string | null;
  dial_number_other: string | null;
  reason: string | null;
  added_at: string;
  added_by: CallListAccount | null;
  person: CallPerson;
  attempts: CallAttempt[];
  also_on: CallListShared[];
}

export interface CallNumber {
  type: CallNumberType;
  label: string | null;
  number: string;
  custom_field_config_id: string | null;
}

export interface CallCompany {
  id: string;
  name: string;
  phone: string | null;
  open_jobs_count: number;
  open_jobs: { id: string; title: string }[];
}

export interface NextCallPreview {
  status: CallListEntryStatus | null;
  next_call_at: string | null;
  requires_choice: boolean;
}

export interface CallListEntryDetail extends CallListEntry {
  numbers: CallNumber[];
  history: CallAttempt[];
  planned: { attempt_number: number; date: string }[];
  previews: {
    connected: NextCallPreview;
    voicemail: NextCallPreview;
    no_answer: NextCallPreview;
    is_last_attempt: boolean;
  };
  company: CallCompany | null;
  other_contacts: { id: string; first_name: string; last_name: string; title: string | null }[];
}

export interface AddCallListEntriesResult {
  added: number;
  reactivated: number;
  skipped_existing: number;
  skipped_dnc: number;
  skipped_not_found: number;
}

export interface LoggedCall {
  entry: CallListEntry;
  attempt: CallAttempt | null;
  note_saved: boolean;
  task_id: string | null;
  next_entry_id: string | null;
}

export interface EntryMoved {
  entry: CallListEntry;
  next_entry_id: string | null;
}

export interface CallListMembership {
  entry_id: string;
  call_list_id: string;
  call_list_name: string;
  status: CallListEntryStatus;
  attempt_number: number;
  max_attempts: number;
  next_call_at: string | null;
  reason: string | null;
  added_at: string;
  added_from: CallListAddedFrom | null;
  added_by: CallListAccount | null;
  attempts: CallAttempt[];
}

export interface CallListMemberships {
  mine: CallListMembership[];
  others: CallListShared[];
}

/** `owner_id`: admins only, to make the list for a team member. */
export type CreateCallListDto = Partial<CallListSettings> & { name: string; owner_id?: string };

export type UpdateCallListDto = Partial<CallListSettings> & { name?: string };

export interface AddCallListEntriesDto {
  entity_type: CallListPersonType;
  entity_ids: string[];
  first_call?: FirstCallWhen;
  first_call_date?: string;
  dial_number_type?: CallNumberType;
  dial_custom_field_config_id?: string;
  dial_number_other?: string;
  reason?: string;
  added_from?: CallListAddedFrom;
}

export interface LogCallDto {
  expected_attempt_number: number;
  outcome: CallOutcome;
  notes?: string;
  started_at?: string;
  duration_seconds?: number;
  dialled_number?: string;
  number_type?: CallNumberType;
  number_label?: string;
  next_call?: { preset: NextCallPreset; date?: string };
  follow_up_task?: { title: string; due_at?: string };
}

export interface UpdateCallListEntryDto {
  dial_number_type?: CallNumberType;
  dial_custom_field_config_id?: string;
  dial_number_other?: string;
  reason?: string;
  next_call_date?: string;
}
